import { useState } from "react";

import { getHeaders } from "../utils/headers";

type ApiResponse<T> = {
  isLoading: boolean;
  error: any | null;
  data: T | null;
};

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

type HTTPHeaders = {
  [key: string]: string;
};

export const useNetwork = <T>(): {
  response: ApiResponse<T>;
  fetchData: (
    url: string,
    method: HttpMethod,
    headers?: HTTPHeaders,
    body?: any,
    isFormData?: boolean,
  ) => Promise<ApiResponse<T>>;
} => {
  const [response, setResponse] = useState<ApiResponse<T>>({
    isLoading: false,
    error: null,
    data: null,
  });

  const fetchData = async (
    url: string,
    method: HttpMethod,
    headers?: HTTPHeaders,
    body?: any,
    isFormData?: boolean,
  ): Promise<ApiResponse<T>> => {
    setResponse({ ...response, isLoading: true });
    const host =
      url.match(/(?<=https:\/\/|http:\/\/)(.*?)(?=\/)/)?.[0] ??
      "qa1.foundit.in";
    const modifiedHeader = getHeaders(host, isFormData);

    try {
      const fetchOptions: RequestInit = {
        method,
        credentials: "include",
        headers: {
          ...modifiedHeader,
          ...headers,
        },
        body: isFormData ? body : JSON.stringify(body),
      };

      const fetchResponse = await fetch(url, fetchOptions);

      const responseData = await fetchResponse.json();

      if (!fetchResponse.ok) {
        setResponse({
          isLoading: false,
          error: responseData,
          data: null,
        });
        return {
          isLoading: false,
          error: responseData,
          data: null,
        };
      }

      setResponse({ isLoading: false, error: null, data: responseData });
      return { isLoading: false, error: null, data: responseData };
    } catch (error: any) {
      setResponse({
        isLoading: false,
        error: error,
        data: null,
      });
      return {
        isLoading: false,
        error: error,
        data: null,
      };
    }
  };

  return { response, fetchData };
};
